import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { SummaryCard, SummaryCardFormData } from '../../types';
import SummaryService from '../../services/summaryService';

interface SummaryState {
  cards: SummaryCard[];
  selectedCard: SummaryCard | null;
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  selectedSubject: string | null;
  sortBy: 'title' | 'subject' | 'difficulty' | 'createdAt' | 'viewCount' | 'likeCount';
  sortOrder: 'asc' | 'desc';
  viewMode: 'grid' | 'list';
  pagination: {
    currentPage: number;
    pageSize: number;
    hasMore: boolean;
    lastDoc: any;
  };
}

const initialState: SummaryState = {
  cards: [],
  selectedCard: null,
  isLoading: false,
  error: null,
  searchQuery: '',
  selectedSubject: null,
  sortBy: 'createdAt',
  sortOrder: 'desc',
  viewMode: 'grid',
  pagination: {
    currentPage: 1,
    pageSize: 12,
    hasMore: true,
    lastDoc: null
  }
};

// Async thunks
export const fetchSummaryCards = createAsyncThunk(
  'summary/fetchSummaryCards',
  async (_, { rejectWithValue }) => {
    try {
      const cards = await SummaryService.getAllSummaryCards();
      return cards;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch summary cards');
    }
  }
);

export const fetchSummaryCardsBySubject = createAsyncThunk(
  'summary/fetchSummaryCardsBySubject',
  async (subject: string, { rejectWithValue }) => {
    try {
      const cards = await SummaryService.getSummaryCardsBySubject(subject);
      return cards;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch summary cards by subject');
    }
  }
);

export const fetchSummaryCardById = createAsyncThunk(
  'summary/fetchSummaryCardById',
  async (id: string, { rejectWithValue }) => {
    try {
      const card = await SummaryService.getSummaryCardById(id);
      return card;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch summary card');
    }
  }
);

export const createSummaryCard = createAsyncThunk(
  'summary/createSummaryCard',
  async ({ data, userId }: { data: SummaryCardFormData; userId: string }, { rejectWithValue }) => {
    try {
      const cardId = await SummaryService.createSummaryCard(data, userId);
      const newCard = await SummaryService.getSummaryCardById(cardId);
      return newCard;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create summary card');
    }
  }
);

export const updateSummaryCard = createAsyncThunk(
  'summary/updateSummaryCard',
  async ({ id, data }: { id: string; data: Partial<SummaryCardFormData> }, { rejectWithValue }) => {
    try {
      await SummaryService.updateSummaryCard(id, data);
      const updatedCard = await SummaryService.getSummaryCardById(id);
      return updatedCard;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update summary card');
    }
  }
);

export const deleteSummaryCard = createAsyncThunk(
  'summary/deleteSummaryCard',
  async (id: string, { rejectWithValue }) => {
    try {
      await SummaryService.deleteSummaryCard(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete summary card');
    }
  }
);

export const searchSummaryCards = createAsyncThunk(
  'summary/searchSummaryCards',
  async (searchTerm: string, { rejectWithValue }) => {
    try {
      const cards = await SummaryService.searchSummaryCards(searchTerm);
      return cards;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to search summary cards');
    }
  }
);

export const incrementViewCount = createAsyncThunk(
  'summary/incrementViewCount',
  async (id: string, { rejectWithValue }) => {
    try {
      await SummaryService.incrementViewCount(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to increment view count');
    }
  }
);

export const toggleLikeSummaryCard = createAsyncThunk(
  'summary/toggleLikeSummaryCard',
  async ({ id, increment }: { id: string; increment: boolean }, { rejectWithValue }) => {
    try {
      await SummaryService.toggleLike(id, increment);
      return { id, increment };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to toggle like');
    }
  }
);

export const fetchSummaryCardsWithPagination = createAsyncThunk(
  'summary/fetchSummaryCardsWithPagination',
  async ({ pageSize, lastDoc }: { pageSize?: number; lastDoc?: any }, { rejectWithValue }) => {
    try {
      const result = await SummaryService.getSummaryCardsWithPagination(pageSize, lastDoc);
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch paginated summary cards');
    }
  }
);

// Admin thunks
export const fetchAllSummaryCardsForAdmin = createAsyncThunk(
  'summary/fetchAllSummaryCardsForAdmin',
  async (_, { rejectWithValue }) => {
    try {
      const cards = await SummaryService.getAllSummaryCardsForAdmin();
      return cards;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch all summary cards for admin');
    }
  }
);

export const toggleSummaryCardPublish = createAsyncThunk(
  'summary/toggleSummaryCardPublish',
  async (id: string, { rejectWithValue }) => {
    try {
      await SummaryService.togglePublishStatus(id);
      const updatedCard = await SummaryService.getSummaryCardById(id);
      return updatedCard;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to toggle publish status');
    }
  }
);

const summarySlice = createSlice({
  name: 'summary',
  initialState,
  reducers: {
    setSelectedCard: (state, action: PayloadAction<SummaryCard | null>) => {
      state.selectedCard = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setSelectedSubject: (state, action: PayloadAction<string | null>) => {
      state.selectedSubject = action.payload;
    },
    setSortBy: (state, action: PayloadAction<typeof initialState.sortBy>) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortOrder = action.payload;
    },
    setViewMode: (state, action: PayloadAction<'grid' | 'list'>) => {
      state.viewMode = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetPagination: (state) => {
      state.pagination = {
        currentPage: 1,
        pageSize: 12,
        hasMore: true,
        lastDoc: null
      };
    },
    setCards: (state, action: PayloadAction<SummaryCard[]>) => {
      state.cards = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch summary cards
      .addCase(fetchSummaryCards.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSummaryCards.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cards = action.payload;
      })
      .addCase(fetchSummaryCards.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch summary cards by subject
      .addCase(fetchSummaryCardsBySubject.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSummaryCardsBySubject.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cards = action.payload;
      })
      .addCase(fetchSummaryCardsBySubject.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch summary card by ID
      .addCase(fetchSummaryCardById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSummaryCardById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedCard = action.payload;
      })
      .addCase(fetchSummaryCardById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Create summary card
      .addCase(createSummaryCard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createSummaryCard.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.cards.unshift(action.payload);
        }
      })
      .addCase(createSummaryCard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Update summary card
      .addCase(updateSummaryCard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSummaryCard.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          const index = state.cards.findIndex(card => card.id === action.payload!.id);
          if (index !== -1) {
            state.cards[index] = action.payload;
          }
          if (state.selectedCard?.id === action.payload.id) {
            state.selectedCard = action.payload;
          }
        }
      })
      .addCase(updateSummaryCard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Delete summary card
      .addCase(deleteSummaryCard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteSummaryCard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cards = state.cards.filter(card => card.id !== action.payload);
        if (state.selectedCard?.id === action.payload) {
          state.selectedCard = null;
        }
      })
      .addCase(deleteSummaryCard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Search summary cards
      .addCase(searchSummaryCards.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchSummaryCards.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cards = action.payload;
      })
      .addCase(searchSummaryCards.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Increment view count
      .addCase(incrementViewCount.fulfilled, (state, action) => {
        const card = state.cards.find(card => card.id === action.payload);
        if (card) {
          card.viewCount += 1;
        }
        if (state.selectedCard?.id === action.payload) {
          state.selectedCard.viewCount += 1;
        }
      })
      
      // Toggle like
      .addCase(toggleLikeSummaryCard.fulfilled, (state, action) => {
        const { id, increment } = action.payload;
        const card = state.cards.find(card => card.id === id);
        if (card) {
          card.likeCount = increment ? card.likeCount + 1 : Math.max(0, card.likeCount - 1);
        }
        if (state.selectedCard?.id === id) {
          state.selectedCard.likeCount = increment 
            ? state.selectedCard.likeCount + 1 
            : Math.max(0, state.selectedCard.likeCount - 1);
        }
      })
      
      // Fetch with pagination
      .addCase(fetchSummaryCardsWithPagination.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSummaryCardsWithPagination.fulfilled, (state, action) => {
        state.isLoading = false;
        const { cards, lastDoc } = action.payload;
        
        if (state.pagination.currentPage === 1) {
          state.cards = cards;
        } else {
          state.cards.push(...cards);
        }
        
        state.pagination.lastDoc = lastDoc;
        state.pagination.hasMore = cards.length === state.pagination.pageSize;
        state.pagination.currentPage += 1;
      })
      .addCase(fetchSummaryCardsWithPagination.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Admin: Fetch all summary cards
      .addCase(fetchAllSummaryCardsForAdmin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllSummaryCardsForAdmin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cards = action.payload;
      })
      .addCase(fetchAllSummaryCardsForAdmin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Toggle summary card publish status
      .addCase(toggleSummaryCardPublish.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(toggleSummaryCardPublish.fulfilled, (state, action) => {
        state.isLoading = false;
        const cardId = action.meta.arg;
        const cardIndex = state.cards.findIndex(card => card.id === cardId);
        if (cardIndex !== -1) {
          state.cards[cardIndex].isPublished = !state.cards[cardIndex].isPublished;
        }
      })
      .addCase(toggleSummaryCardPublish.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  }
});

export const {
  setSelectedCard,
  setSearchQuery,
  setSelectedSubject,
  setSortBy,
  setSortOrder,
  setViewMode,
  clearError,
  resetPagination,
  setCards
} = summarySlice.actions;

export default summarySlice.reducer;